import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { teacherDisplayName } from "./signoutData";
import useActiveEmergency from "../hooks/useActiveEmergency";
import useEmergencyCheckIns from "../hooks/useEmergencyCheckIns";
import useFetchClasses from "../hooks/useTeacherClasses";
import { endEmergency, setEmergencyKind, checkInStudent, undoCheckIn, checkInMany } from "../shared/emergencyData";
import { activeClassOf } from "../shared/activeClass";
import ChecklistMode from "./ChecklistMode";
import EmergencyRecap from "./EmergencyRecap";
import StudentCheckRow from "./StudentCheckRow";
import { byLastName } from "../shared/nameSort";
import "./Checklist.css";

// ── Helpers ──────────────────────────────────────────────────────────────────
const byName = byLastName;

function fmtTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const h = d.getHours(), m = d.getMinutes();
  return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

// ── Entry: route Check List tab to the everyday checklist or the active emergency ──
export default function EmergencyView() {
  const { emergency, loading } = useActiveEmergency();
  const { role } = useUser();
  const [recap, setRecap] = useState(null); // the just-ended emergency to recap
  const prevRef = useRef(null);

  // Detect the active emergency ending (present → null on the live snapshot) and
  // capture it, so afterward admins get a recap and everyone else a "done" banner.
  // endedAt is approximated as now (exact value is read back in the history list).
  useEffect(() => {
    if (loading) return;
    const prev = prevRef.current;
    if (prev && !emergency) {
      setRecap({ ...prev, isActive: false, endedAt: prev.endedAt || new Date().toISOString() });
    }
    prevRef.current = emergency;
  }, [emergency, loading]);

  if (loading) return <div className="checklist-page"><p>Loading…</p></div>;
  if (emergency) return <EmergencyMode emergency={emergency} />;
  if (recap) {
    return role === "admin"
      ? <EmergencyRecap emergency={recap} onClose={() => setRecap(null)} />
      : <EmergencyDoneBanner isDrill={recap.isDrill} onClose={() => setRecap(null)} />;
  }
  return <ChecklistMode />;
}

// Shown to non-admin staff once a drill/emergency ends (the full recap is admin-only).
function EmergencyDoneBanner({ isDrill, onClose }) {
  return (
    <div className="checklist-page">
      <div className="emg-done-banner">
        <div className="emg-done-icon">✓</div>
        <h2 style={{ margin: "8px 0 4px" }}>{isDrill ? "Drill complete" : "All clear"}</h2>
        <p style={{ color: "var(--color-secondary)", maxWidth: 420 }}>
          The {isDrill ? "drill" : "emergency"} has ended. Thanks for accounting for your students.
        </p>
        <button className="btn-secondary" onClick={onClose} style={{ marginTop: 12 }}>
          Back to Check List
        </button>
      </div>
    </div>
  );
}

// ── Active emergency: school-wide accountability ─────────────────────────────
function EmergencyMode({ emergency }) {
  const { user, uid, schoolId, teacherName } = useUser();
  const checkIns = useEmergencyCheckIns(emergency.id);
  const { classList } = useFetchClasses();
  const myClassIds = useMemo(() => new Set(classList.map((c) => c.id)), [classList]);

  const [view, setView] = useState("class"); // class | master
  const [students, setStudents] = useState([]);
  const [classMap, setClassMap] = useState({});   // classId -> { className, teacherId }
  const [teacherMap, setTeacherMap] = useState({}); // uid -> name
  const [expandedIds, setExpandedIds] = useState(() => new Set()); // expanded cards (Master)
  const [browseClassId, setBrowseClassId] = useState(""); // selected class (By class)
  const [userPicked, setUserPicked] = useState(false);

  // Load the whole-school roster, classes, and teacher names (schoolId-scoped).
  useEffect(() => {
    if (!schoolId) return;
    let alive = true;
    (async () => {
      try {
        const [sSnap, cSnap, uSnap] = await Promise.all([
          getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId))),
          getDocs(query(collection(firestore, "classes"), where("schoolId", "==", schoolId))),
          getDocs(query(collection(firestore, "users"), where("schoolId", "==", schoolId))),
        ]);
        if (!alive) return;
        setStudents(sSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const cm = {};
        cSnap.docs.forEach((d) => {
          const dd = d.data();
          cm[d.id] = { className: dd.className || "Class", teacherId: dd.teacherId, isArchived: dd.isArchived === true, isHomeroom: dd.isHomeroom === true, isSpecials: dd.classType === "specials" };
        });
        setClassMap(cm);
        const tm = {};
        uSnap.docs.forEach((d) => { tm[d.id] = teacherDisplayName(d.data()); });
        setTeacherMap(tm);
      } catch (e) {
        console.error("[EmergencyMode] load error:", e);
      }
    })();
    return () => { alive = false; };
  }, [schoolId, emergency.id]);

  const classNameOf = (cid) =>
    classMap[cid]?.className || (cid === "unassigned" ? "Unassigned" : "Class");
  const teacherForClass = (cid) => teacherMap[classMap[cid]?.teacherId] || "—";

  const tap = (student) => {
    const ci = checkIns[student.id];
    if (ci && ci.checkedOffByTeacherId === uid) undoCheckIn(emergency.id, student.id);
    else checkInStudent(emergency.id, student, user, teacherName);
  };

  // Only currently-registered (non-archived) classes count. A student's class is
  // their homeroom if it's active, else their first active enrolled class; students
  // with no active class (left over from archived classes) are excluded entirely.
  const activeClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => !classMap[id].isArchived)),
    [classMap],
  );
  // Classes a teacher flagged as their homeroom (Classes page checkbox).
  const homeroomClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => classMap[id].isHomeroom && !classMap[id].isArchived)),
    [classMap],
  );
  const specialsClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => classMap[id].isSpecials)),
    [classMap],
  );
  // Which class a student counts under (explicit homeroom → homeroom-flagged class →
  // first active non-specials enrolled → specials only) — shared with the recap,
  // absent board, and roster so they always agree. See shared/activeClass.js.
  const classOf = (s) => activeClassOf(s, activeClassIds, homeroomClassIds, specialsClassIds);
  const visibleStudents = useMemo(
    () => students.filter((s) => classOf(s) !== null),
    [students, activeClassIds], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Group the visible roster by active class for the per-class view + counts.
  const groups = useMemo(() => {
    const g = {};
    for (const s of visibleStudents) {
      const cid = classOf(s);
      (g[cid] = g[cid] || []).push(s);
    }
    return g;
  }, [visibleStudents]); // eslint-disable-line react-hooks/exhaustive-deps
  const accounted = (s) => !!checkIns[s.id];
  const classDoneCount = (cid) => (groups[cid] || []).filter(accounted).length;
  const classComplete = (cid) => {
    const list = groups[cid] || [];
    return list.length > 0 && classDoneCount(cid) === list.length;
  };

  // Master order: not-fully-accounted first, then your classes, then A–Z. Completed
  // classes sink to the bottom so the ones still needing attention stay on top.
  const sortedGroupIds = Object.keys(groups).sort((a, b) => {
    const ac = classComplete(a) ? 1 : 0;
    const bc = classComplete(b) ? 1 : 0;
    if (ac !== bc) return ac - bc;
    const aMine = myClassIds.has(a) ? 0 : 1;
    const bMine = myClassIds.has(b) ? 0 : 1;
    if (aMine !== bMine) return aMine - bMine;
    return classNameOf(a).localeCompare(classNameOf(b));
  });

  // By-class dropdown order: your classes first, then the rest of the school A–Z.
  const dropdownGroupIds = Object.keys(groups).sort((a, b) => {
    const aMine = myClassIds.has(a) ? 0 : 1;
    const bMine = myClassIds.has(b) ? 0 : 1;
    if (aMine !== bMine) return aMine - bMine;
    return classNameOf(a).localeCompare(classNameOf(b));
  });
  const myGroupIds = dropdownGroupIds.filter((id) => myClassIds.has(id));
  const otherGroupIds = dropdownGroupIds.filter((id) => !myClassIds.has(id));

  // Default the dropdown to your homeroom (if you flagged one and it has students),
  // otherwise your first class — until you pick one manually.
  const myHomeroomId = [...myClassIds].find((id) => homeroomClassIds.has(id) && groups[id]);
  const defaultBrowseId = myHomeroomId || dropdownGroupIds[0];
  useEffect(() => {
    if (!userPicked && defaultBrowseId) setBrowseClassId(defaultBrowseId);
  }, [defaultBrowseId, userPicked]);

  const toggleExpand = (cid) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });

  const checkInAll = (cid) => {
    const remaining = (groups[cid] || []).filter((s) => !accounted(s));
    if (remaining.length) checkInMany(emergency.id, remaining, user, teacherName);
  };

  const schoolDone = visibleStudents.filter(accounted).length;
  const pct = visibleStudents.length ? Math.round((schoolDone / visibleStudents.length) * 100) : 0;

  const subFor = (s) => {
    const ci = checkIns[s.id];
    if (ci) {
      const who = ci.checkedOffByTeacherId === uid ? "you" : (ci.checkedOffByTeacherName || "another teacher");
      return `Accounted ${fmtTime(ci.timestamp)} · by ${who}`;
    }
    const cid = classOf(s);
    return `${classNameOf(cid)} · ${teacherForClass(cid)}`;
  };
  const toneFor = (s) => {
    const ci = checkIns[s.id];
    if (!ci) return "success";
    return ci.checkedOffByTeacherId === uid ? "success" : "info";
  };


  return (
    <div className="checklist-page">
      <div className="checklist-head">
        <div className="emg-mode-head">
          <div>
            <h2 style={{ margin: 0 }}>{emergency.isDrill ? "🟦 Drill Active" : "🚨 Emergency Active"}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
              <span className="emg-live-badge"><i /> LIVE</span>
              <span className={`emg-kind-badge ${emergency.isDrill ? "is-drill" : "is-real"}`}>
                {emergency.isDrill ? "DRILL" : "EMERGENCY"}
              </span>
              <button
                className="emg-kind-toggle"
                onClick={() => setEmergencyKind(emergency.id, !emergency.isDrill)}
                title="Switch between drill and real emergency"
              >
                Mark as {emergency.isDrill ? "real emergency" : "drill"}
              </button>
              <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>
                Started by {emergency.startedByName || "staff"} at {fmtTime(emergency.startedAt)}
              </span>
            </div>
          </div>
          <button
            className="emg-end-btn"
            onClick={() => { if (window.confirm("End the emergency? This stops tracking and resets for next time.")) endEmergency(emergency.id); }}
          >
            End Emergency
          </button>
        </div>

        <div className="emg-progress-label">School-wide · {schoolDone} / {visibleStudents.length} accounted ({pct}%)</div>
        <div className="emg-progress"><span style={{ width: `${pct}%` }} /></div>

        <div className="seg">
          <button className={view === "class" ? "active" : ""} onClick={() => setView("class")}>By class</button>
          <button className={view === "master" ? "active" : ""} onClick={() => setView("master")}>Master (school-wide)</button>
        </div>

        {view === "class" && (
          <select
            className="checklist-class-select"
            value={browseClassId}
            onChange={(e) => { setBrowseClassId(e.target.value); setUserPicked(true); }}
          >
            {myGroupIds.length > 0 && (
              <optgroup label="My classes">
                {myGroupIds.map((cid) => (
                  <option key={cid} value={cid}>
                    {classNameOf(cid)} ({classDoneCount(cid)}/{groups[cid].length})
                  </option>
                ))}
              </optgroup>
            )}
            {otherGroupIds.length > 0 && (
              <optgroup label="School (A–Z)">
                {otherGroupIds.map((cid) => (
                  <option key={cid} value={cid}>
                    {classNameOf(cid)} ({classDoneCount(cid)}/{groups[cid].length})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        )}
      </div>

      {view === "class" ? (
        (() => {
          if (!browseClassId) return <p className="checklist-empty">No classes to show.</p>;
          const list = [...(groups[browseClassId] || [])].sort(byName);
          const done = classDoneCount(browseClassId);
          const complete = classComplete(browseClassId);
          return (
            <>
              <div className="emg-progress-label" style={{ marginTop: 4 }}>
                {classNameOf(browseClassId)} · {done} / {list.length} accounted
              </div>
              {list.length > 0 && !complete && (
                <button className="emg-checkall" onClick={() => checkInAll(browseClassId)}>
                  Check in all ({list.length - done})
                </button>
              )}
              <div className="check-list" style={{ marginTop: "var(--space-2)" }}>
                {list.map((s) => (
                  <StudentCheckRow
                    key={s.id}
                    name={s.studentName}
                    subtitle={subFor(s)}
                    checked={accounted(s)}
                    tone={toneFor(s)}
                    onClick={() => tap(s)}
                  />
                ))}
                {list.length === 0 && <p className="checklist-empty">No students in this class.</p>}
              </div>
            </>
          );
        })()
      ) : (
        <div className="emg-class-list">
          {sortedGroupIds.map((cid) => {
            const list = [...(groups[cid] || [])].sort(byName);
            const done = classDoneCount(cid);
            const complete = classComplete(cid);
            const cpct = list.length ? Math.round((done / list.length) * 100) : 0;
            const expanded = expandedIds.has(cid);
            return (
              <div key={cid} className={`emg-class-card ${complete ? "is-complete" : ""}`}>
                <button className="emg-class-head" onClick={() => toggleExpand(cid)}>
                  <span className="emg-chevron">{expanded ? "▾" : "▸"}</span>
                  <span className="emg-class-info">
                    <span className="emg-class-name">{classNameOf(cid)}</span>
                    <span className="emg-class-teacher">{teacherForClass(cid)}</span>
                  </span>
                  <span className={`emg-class-count ${complete ? "done" : ""}`}>
                    {done}/{list.length}{complete ? " ✓" : ""}
                  </span>
                </button>
                <div className="emg-progress"><span style={{ width: `${cpct}%` }} /></div>
                {!complete && (
                  <button className="emg-checkall" onClick={() => checkInAll(cid)}>
                    Check in all ({list.length - done})
                  </button>
                )}
                {expanded && (
                  <div className="check-list" style={{ marginTop: "var(--space-2)" }}>
                    {list.map((s) => (
                      <StudentCheckRow
                        key={s.id}
                        name={s.studentName}
                        subtitle={subFor(s)}
                        checked={accounted(s)}
                        tone={toneFor(s)}
                        onClick={() => tap(s)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {sortedGroupIds.length === 0 && <p className="checklist-empty">No classes to show.</p>}
        </div>
      )}
    </div>
  );
}
